import React, { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { COLORS } from '../../utils/constants';
import { useLanguage } from '../../context/LanguageContext';
import { X, Wallet, Loader2, CheckCircle, AlertCircle, Smartphone, Copy, Check } from 'lucide-react';

const WalletConnectModal = ({ isOpen, onClose, onConnect, connectedWallets = [] }) => {
  const { t } = useLanguage();
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [qrCodeUri, setQrCodeUri] = useState(null);
  const [copied, setCopied] = useState(false);
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  // Check device type on mount
  useEffect(() => {
    const checkMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsMobileDevice(checkMobile);
  }, []);

  // Lock/unlock body scroll when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setQrCodeUri(null);
      setError(null);
      setConnecting(false);
      setCopied(false);
    }
  }, [isOpen]);

  const handleClose = useCallback(() => {
    setQrCodeUri(null);
    setError(null);
    setConnecting(false);
    onClose();
  }, [onClose]);

  // Handle Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleConnect = async () => {
    console.log('WalletConnectModal: Starting WalletConnect connection...');
    setConnecting(true);
    setError(null);
    setQrCodeUri(null);
    
    try {
      // Call onConnect which will set up event listeners and return the URI
      const result = await onConnect('walletconnect');
      
      // If we got a URI, show QR code
      if (result && result.uri) {
        console.log('WalletConnectModal: Got QR URI');
        setQrCodeUri(result.uri);
      } else if (result && result.address) {
        // Already connected
        console.log('WalletConnectModal: Already connected to', result.address);
        handleClose();
      }
    } catch (err) {
      console.error('WalletConnectModal: connection error:', err);
      if (err.message !== 'WAITING_FOR_QR') {
        setError(err.message || t('failedToConnectWallet') || 'Ошибка подключения');
        setConnecting(false);
      }
    }
  };

  // Copy URI to clipboard
  const copyToClipboard = async () => {
    if (qrCodeUri) {
      try {
        await navigator.clipboard.writeText(qrCodeUri);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  // Open deep link for mobile
  const openMobileWallet = (walletScheme) => {
    if (qrCodeUri) {
      const encodedUri = encodeURIComponent(qrCodeUri);
      window.location.href = `${walletScheme}wc?uri=${encodedUri}`;
    }
  };

  if (!isOpen) return null;

  const isSmallScreen = typeof window !== 'undefined' && window.innerWidth < 768;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.85)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: isSmallScreen ? '16px' : '20px',
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <div
        style={{
          backgroundColor: COLORS.card,
          borderRadius: isSmallScreen ? '16px' : '20px',
          width: '100%',
          maxWidth: isSmallScreen ? '100%' : '420px',
          maxHeight: '90vh',
          overflow: 'auto',
          border: `1px solid ${COLORS.border}`,
        }}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: isSmallScreen ? '16px' : '20px',
            borderBottom: `1px solid ${COLORS.border}`,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <Wallet size={24} color={COLORS.primary} />
            <h2 style={{ 
              margin: 0, 
              fontSize: isSmallScreen ? '18px' : '20px', 
              fontWeight: '600', 
              color: COLORS.text 
            }}>
              {t('connectWallet') || 'Подключить кошелёк'}
            </h2>
          </div>
          <button
            onClick={handleClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={24} color={COLORS.textSecondary} />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: isSmallScreen ? '16px' : '24px' }}>
          {/* Connected wallets count */}
          {connectedWallets.length > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: `${COLORS.success}15`,
              borderRadius: '8px',
              border: `1px solid ${COLORS.success}30`,
            }}>
              <CheckCircle size={18} color={COLORS.success} />
              <span style={{ color: COLORS.success, fontSize: '14px' }}>
                {t('connectedWallets') || 'Подключено кошельков'}: {connectedWallets.length}
              </span>
            </div>
          )}

          {/* Error message */}
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: `${COLORS.danger}15`,
              borderRadius: '8px',
              border: `1px solid ${COLORS.danger}30`,
            }}>
              <AlertCircle size={18} color={COLORS.danger} />
              <span style={{ color: COLORS.danger, fontSize: '14px' }}>{error}</span>
            </div>
          )}

          {/* QR Code Display */}
          {qrCodeUri ? (
            <div style={{ textAlign: 'center' }}>
              <p style={{ 
                color: COLORS.textSecondary, 
                fontSize: '14px', 
                marginBottom: '20px',
                lineHeight: '1.5',
              }}>
                {isMobileDevice 
                  ? 'Выберите кошелёк или скопируйте ссылку'
                  : 'Отсканируйте QR-код камерой вашего мобильного кошелька'
                }
              </p>
              
              {/* QR Code */}
              {!isMobileDevice && (
                <div style={{
                  backgroundColor: '#fff',
                  padding: '20px',
                  borderRadius: '16px',
                  display: 'inline-block',
                  marginBottom: '20px',
                }}>
                  <QRCodeSVG 
                    value={qrCodeUri} 
                    size={220}
                    level="M"
                    includeMargin={false}
                  />
                </div>
              )}

              {/* Mobile wallet buttons */}
              {isMobileDevice && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '16px' }}>
                  <button
                    onClick={() => openMobileWallet('metamask://')}
                    style={{
                      padding: '14px',
                      backgroundColor: '#f6851b15',
                      border: '1px solid #f6851b40',
                      borderRadius: '12px',
                      color: COLORS.text,
                      fontSize: '15px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                    }}
                  >
                    🦊 MetaMask
                  </button>
                  <button
                    onClick={() => openMobileWallet('trust://')}
                    style={{
                      padding: '14px',
                      backgroundColor: '#3375bb15',
                      border: '1px solid #3375bb40',
                      borderRadius: '12px',
                      color: COLORS.text,
                      fontSize: '15px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                    }}
                  >
                    🛡️ Trust Wallet
                  </button>
                  <button
                    onClick={() => openMobileWallet('rainbow://')}
                    style={{
                      padding: '14px',
                      backgroundColor: '#88419d15',
                      border: '1px solid #88419d40',
                      borderRadius: '12px',
                      color: COLORS.text,
                      fontSize: '15px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '10px',
                    }}
                  >
                    🌈 Rainbow
                  </button>
                </div>
              )}

              {/* Copy button */}
              <button
                onClick={copyToClipboard}
                style={{
                  padding: '12px 20px',
                  backgroundColor: COLORS.background,
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: '8px',
                  color: COLORS.text,
                  fontSize: '14px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  width: '100%',
                }}
              >
                {copied ? <Check size={18} color={COLORS.success} /> : <Copy size={18} />}
                {copied ? 'Скопировано!' : 'Скопировать ссылку'}
              </button>
            </div>
          ) : (
            <>
              {/* Description */}
              <p style={{ 
                color: COLORS.textSecondary, 
                fontSize: '14px', 
                marginBottom: '20px',
                textAlign: 'center',
                lineHeight: '1.5',
              }}>
                {isMobileDevice 
                  ? 'Нажмите кнопку для подключения кошелька'
                  : 'Отсканируйте QR-код камерой мобильного кошелька'
                }
              </p>

              {/* Supported wallets */}
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: '8px',
                marginBottom: '20px',
              }}>
                {['MetaMask', 'Trust Wallet', 'Rainbow', 'Argent', '+300'].map((wallet) => (
                  <span
                    key={wallet}
                    style={{
                      padding: '4px 10px',
                      backgroundColor: COLORS.background,
                      borderRadius: '12px',
                      fontSize: '12px',
                      color: COLORS.textSecondary,
                    }}
                  >
                    {wallet}
                  </span>
                ))}
              </div>

              {/* Connect button */}
              <button
                onClick={handleConnect}
                disabled={connecting}
                style={{
                  width: '100%',
                  padding: '16px',
                  backgroundColor: connecting ? COLORS.textSecondary : COLORS.primary,
                  color: '#000',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: connecting ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  transition: 'background-color 0.2s',
                }}
              >
                {connecting ? (
                  <>
                    <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
                    {t('connecting') || 'Подключение...'}
                  </>
                ) : (
                  <>
                    <Smartphone size={20} />
                    {connectedWallets.length > 0 
                      ? (t('addAnotherWallet') || 'Добавить ещё кошелёк')
                      : (t('connectWithQR') || 'Подключить через QR')
                    }
                  </>
                )}
              </button>
            </>
          )}

          {/* Help text */}
          <p style={{
            color: COLORS.textSecondary,
            fontSize: '12px',
            textAlign: 'center',
            marginTop: '16px',
            lineHeight: '1.4',
          }}>
            {t('walletConnectHelp') || 'Поддерживается 300+ кошельков через WalletConnect'}
          </p>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default WalletConnectModal;
